import React from 'react';
import type { ClientAmendmentCorrectionDto, ClientAmendmentDto } from '../../lib/quotationApi';
import Button from '../../components/ui/Button';
import { ValueChange } from '../../components/ui/ValueChange';
import { TONE } from '../../components/ui/tokens';
import { formatDate } from '../../utils/dateFormat';

/**
 * "Client asked for changes on <date> — applied to QUO-00068-1. Review before sending."
 *
 * D45.1: a client's email asking to change a quotation is applied, and a person reviews it before
 * the quotation goes out. One banner per change, on the RFQ screen and on the quotation screen.
 * Which changes are still waiting is the SERVER's answer (`pendingClientAmendments`, owner
 * `quotationAmendment.service`), so the two screens cannot disagree; this only draws it.
 *
 * Every line the change touched is listed, including a unit change that was NOT applied — the
 * line's price is per its own unit, so a person restates it on the grid.
 */
export interface ClientAmendmentBannerProps {
  amendments: ClientAmendmentDto[] | null | undefined;
  /** Opens the quotation the change was applied to. Omitted on the quotation's own screen. */
  onOpenQuotation?: (quotationId: string) => void;
  /** Marks one change reviewed. Omitted where the screen does not offer it. */
  onMarkReviewed?: (amendmentId: string) => void | Promise<void>;
  /** The change being marked right now, if any. */
  reviewingId?: string | null;
}

const amount = (quantity: number | null, uom: string | null) =>
  [quantity == null ? '' : String(quantity), uom || ''].filter(Boolean).join(' ') || 'no quantity given';

const lineLabel = (lineNumber: number | null) => (lineNumber == null ? '' : ` (line ${lineNumber})`);

function CorrectionText({ change }: { change: ClientAmendmentCorrectionDto }) {
  const from = amount(change.oldQuantity, change.oldUom);
  const to = amount(change.newQuantity, change.newUom);
  if (!change.applied) {
    return (
      <>
        {change.itemName}
        {lineLabel(change.lineNumber)}: asked for <strong>{to}</strong> — not applied, the line is priced per{' '}
        {change.oldUom || 'its own unit'}. Change its unit on the quotation.
      </>
    );
  }
  return (
    <>
      {change.itemName}
      {lineLabel(change.lineNumber)}: <ValueChange from={from} to={to} />
    </>
  );
}

export const ClientAmendmentBanner: React.FC<ClientAmendmentBannerProps> = ({
  amendments,
  onOpenQuotation,
  onMarkReviewed,
  reviewingId = null,
}) => {
  const list = amendments || [];
  if (!list.length) return null;

  return (
    <div className="space-y-2">
      {list.map((a) => {
        const when = formatDate(a.receivedAt || a.appliedAt, 'an unknown date');
        return (
          <div
            key={a._id}
            role="status"
            aria-label={`Client changes to ${a.quotationNumber}`}
            className={`rounded-md border px-4 py-3 ${TONE.attention}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 text-sm">
                <div className="font-semibold">
                  Client asked for changes on {when} — applied to {a.quotationNumber}. Review before sending.
                </div>
                {a.mode === 'new_version' && (
                  <div className="mt-0.5 text-xs">
                    The quotation they had been sent is kept as they saw it; {a.quotationNumber} is a new version.
                  </div>
                )}
                {a.addedItems.length > 0 && (
                  <div className="mt-1.5">
                    <div className="text-xs font-semibold">Added</div>
                    <ul className="mt-0.5 list-disc space-y-0.5 pl-5 text-xs">
                      {a.addedItems.map((it, i) => (
                        <li key={`added-${i}`}>
                          {it.itemName}
                          {lineLabel(it.lineNumber)}: {amount(it.quantity, it.uom)}
                          {!it.priced && <strong> — no price found, price it before sending</strong>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {a.correctedItems.length > 0 && (
                  <div className="mt-1.5">
                    <div className="text-xs font-semibold">Changed</div>
                    <ul className="mt-0.5 list-disc space-y-0.5 pl-5 text-xs">
                      {a.correctedItems.map((change, i) => (
                        <li key={`changed-${i}`}>
                          <CorrectionText change={change} />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {(onOpenQuotation || onMarkReviewed) && (
                <div className="flex shrink-0 items-center gap-2">
                  {onOpenQuotation && (
                    <Button size="sm" onClick={() => onOpenQuotation(a.quotationId)}>
                      Review {a.quotationNumber}
                    </Button>
                  )}
                  {onMarkReviewed && (
                    <Button
                      size="sm"
                      onClick={() => onMarkReviewed(a._id)}
                      loading={reviewingId === a._id}
                      disabled={reviewingId != null}
                    >
                      Mark reviewed
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ClientAmendmentBanner;
